import React, { useEffect, useState } from "react";
import {
  Building2, Users, CreditCard, ChevronRight,
  TrendingUp, Activity, Plus, Search,
  ArrowUpRight, School, UserCheck, Zap,
  Layers, Database, BarChart3, Globe, Shield
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { getApiUrl } from "../../config/api";
import "../../styles/DashboardLayout.css";

interface DashboardStats {
  totalSchools: number;
  activeSchools: number;
  trialSchools: number;
  totalSubscriptions: number;
  activeSubscriptions: number;
  globalAdmins: number;
  globalTeachers: number;
  globalStudents: number;
}

interface RecentSchool {
  id: number;
  name: string;
  subdomain: string;
  status: string;
  created_at: string;
}

const SuperAdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentSchools, setRecentSchools] = useState<RecentSchool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const [statsRes, schoolsRes] = await Promise.all([
        axios.get(getApiUrl("superadmin/stats"), { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(getApiUrl("superadmin/schools"), { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setStats(statsRes.data);
      setRecentSchools(schoolsRes.data.slice(0, 5));
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusStyle = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
      case 'trial': return 'bg-amber-50 text-amber-600 border-amber-100';
      case 'suspended': return 'bg-rose-50 text-rose-600 border-rose-100';
      default: return 'bg-slate-50 text-slate-500 border-slate-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50/50">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-teal-500/20 border-t-teal-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400 text-sm font-medium tracking-wide">Syncing Platform State...</p>
        </div>
      </div>
    );
  }

  if ((stats?.totalSchools || 0) === 0) {
    return (
      <div className="min-h-screen bg-slate-50/50 p-6 lg:p-10">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-[2.25rem] border border-slate-200 bg-white p-8 md:p-12 shadow-sm">
            <div className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-4 py-2 text-[11px] font-black uppercase tracking-[0.24em] text-teal-700">
              <SparklesFallback />
              First Run
            </div>
            <h1 className="mt-6 text-4xl font-black tracking-tight text-slate-950">Bootstrap the platform</h1>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-600">
              This installation is clean: no schools have been provisioned yet. Create the first school tenant and its initial administrator to bring the portal online.
            </p>

            <div className="mt-8 grid gap-4 md:grid-cols-3">
              <BootstrapStep
                index="01"
                title="Create the first school"
                description="Set the school identity, subdomain, subscription tier, quotas, and academic defaults."
              />
              <BootstrapStep
                index="02"
                title="Provision an admin"
                description="The school form also creates the first school administrator and login credentials."
              />
              <BootstrapStep
                index="03"
                title="Finish deployment tuning"
                description="After creation, refine modules, branding, and subscription settings from the edit screen."
              />
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <button
                onClick={() => navigate("/superadmin/schools/new?bootstrap=1")}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                <Plus size={18} />
                Start Bootstrap
              </button>
              <button
                onClick={fetchDashboardData}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <Activity size={16} />
                Refresh State
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 lg:p-10">
      <div className="max-w-[1600px] mx-auto">
        {/* Header Section */}
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">System <span className="text-teal-600 italic font-medium">Overview</span></h1>
            <p className="text-slate-500 mt-1">Real-time health and growth metrics for the multi-school platform.</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/superadmin/audit-trail")}
              className="px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all flex items-center gap-2 shadow-sm"
            >
              <Activity size={16} /> System Logs
            </button>
            <button
              onClick={() => navigate("/superadmin/schools/new")}
              className="px-6 py-2.5 bg-slate-900 text-white rounded-xl text-sm font-semibold hover:bg-slate-800 transition-all flex items-center gap-2 shadow-lg shadow-slate-200"
            >
              <Plus size={18} /> New School
            </button>
          </div>
        </header>

        {error && (
          <div className="mb-8 p-4 bg-rose-50 border border-rose-100 rounded-2xl text-rose-600 text-sm font-medium flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
            {error}
          </div>
        )}

        {/* Primary Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <StatCard
            title="Registered Schools"
            value={stats?.totalSchools || 0}
            subtext={`${stats?.activeSchools || 0} active, ${stats?.trialSchools || 0} trailing`}
            icon={<School size={22} className="text-teal-600" />}
            color="teal"
          />
          <StatCard
            title="Total Platform Users"
            value={stats?.globalStudents || 0}
            subtext={`${stats?.globalTeachers || 0} educators, ${stats?.globalAdmins || 0} admins`}
            icon={<Users size={22} className="text-indigo-600" />}
            color="indigo"
          />
          <StatCard
            title="Revenue Pipeline"
            value={stats?.activeSubscriptions || 0}
            subtext="Subscriptions active"
            icon={<CreditCard size={22} className="text-emerald-600" />}
            color="emerald"
          />
          <StatCard
            title="Health Score"
            value="98.4%"
            subtext="Uptime last 30 days"
            icon={<Zap size={22} className="text-amber-500" />}
            color="amber"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-8">
            {/* Quick Actions / Management Grid */}
            <section>
              <h2 className="text-lg font-bold text-slate-800 mb-5 flex items-center gap-2">
                <Layers size={18} className="text-slate-400" /> Platform Infrastructure
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ManagementLink
                  to="/superadmin/schools"
                  title="Institute Registry"
                  description="Deploy and manage school instances"
                  icon={<Building2 size={20} />}
                  color="teal"
                />
                <ManagementLink
                  to="/superadmin/users"
                  title="Identity Control"
                  description="Global user lookup and auditing"
                  icon={<Shield size={20} />}
                  color="indigo"
                />
                <ManagementLink
                  to="/superadmin/subscriptions"
                  title="Billing & Subs"
                  description="Subscription cycles and revenue"
                  icon={<CreditCard size={20} />}
                  color="emerald"
                />
                <ManagementLink
                  to="/superadmin/analytics"
                  title="Growth Engine"
                  description="Predictive analytics and scaling"
                  icon={<TrendingUp size={20} />}
                  color="blue"
                />
                <ManagementLink
                  to="/superadmin/audit-trail"
                  title="Log Explorer"
                  description="Security events and process history"
                  icon={<Activity size={20} />}
                  color="slate"
                />
                <ManagementLink
                  to="/superadmin/subjects"
                  title="Subject Master"
                  description="Global academic templates"
                  icon={<Layers size={20} />}
                  color="violet"
                />
              </div>
            </section>

            {/* Recent School Deployment */}
            <section>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <Database size={18} className="text-slate-400" /> Recent Deployments
                </h2>
                <Link to="/superadmin/schools" className="text-teal-600 text-sm font-bold hover:underline">View All Registry</Link>
              </div>
              <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden text-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-slate-50/50 border-b border-slate-50">
                        <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Instance</th>
                        <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Environment</th>
                        <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider text-right px-8">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {recentSchools.map(school => (
                        <tr key={school.id} className="group hover:bg-slate-50/30 transition-colors cursor-pointer" onClick={() => navigate(`/superadmin/schools/${school.id}`)}>
                          <td className="px-6 py-4">
                            <span className="text-sm font-bold text-slate-700">{school.name}</span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1.5 text-slate-400">
                              <Globe size={12} />
                              <span className="text-xs font-medium">{school.subdomain}.portal.com</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${getStatusStyle(school.status)}`}>
                              {school.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right px-8">
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/superadmin/schools/${school.id}`); }}
                              className="text-slate-400 hover:text-teal-600 transition-colors"
                            >
                              <ChevronRight size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-8">
            <section className="bg-slate-900 rounded-[2rem] p-8 text-white relative overflow-hidden shadow-2xl shadow-slate-200">
              <div className="relative z-10">
                <h3 className="text-xl font-bold mb-4">Ecosystem Scale</h3>
                <div className="space-y-6 text-sm">
                  <ProgressItem label="Active Students" value={stats?.globalStudents || 0} total={10000} color="bg-teal-400" />
                  <ProgressItem label="Educator Network" value={stats?.globalTeachers || 0} total={1000} color="bg-blue-400" />
                  <ProgressItem label="School Clusters" value={stats?.totalSchools || 0} total={100} color="bg-indigo-400" />
                </div>
                <div className="mt-10 p-4 bg-white/10 rounded-2xl border border-white/10">
                  <div className="flex items-center gap-3 mb-2">
                    <BarChart3 size={18} className="text-teal-400" />
                    <span className="text-xs font-bold uppercase tracking-widest text-teal-400">Trial Conversion</span>
                  </div>
                  <p className="text-2xl font-black">74.2%</p>
                  <p className="text-xs text-slate-400 mt-1">Growth up by 8.4% this qtr</p>
                </div>
              </div>
              <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl"></div>
              <div className="absolute -top-20 -left-20 w-48 h-48 bg-indigo-500/10 rounded-full blur-2xl"></div>
            </section>

            <section className="bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm">
              <h3 className="text-lg font-bold text-slate-800 mb-6 font-bold">Platform Pulse</h3>
              <div className="space-y-4">
                <PulseItem icon={<Globe size={14} />} title="Global Latency" value="42ms" color="teal" />
                <PulseItem icon={<Database size={14} />} title="DB Load" value="12%" color="indigo" />
                <PulseItem icon={<Activity size={14} />} title="Error Rate" value="0.02%" color="emerald" />
              </div>
              <button
                onClick={() => navigate("/superadmin/analytics")}
                className="w-full mt-8 py-3 rounded-2xl bg-slate-50 text-slate-600 text-[10px] font-black uppercase tracking-widest hover:bg-slate-100 transition-all border border-slate-100"
              >
                Detailed Health Report
              </button>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

const SparklesFallback = () => (
  <span className="inline-block h-2.5 w-2.5 rounded-full bg-teal-500"></span>
);

const BootstrapStep = ({ index, title, description }: { index: string; title: string; description: string }) => (
  <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50/70 p-5">
    <div className="text-[11px] font-black uppercase tracking-[0.24em] text-slate-400">{index}</div>
    <h2 className="mt-3 text-lg font-bold text-slate-900">{title}</h2>
    <p className="mt-2 text-sm leading-6 text-slate-600">{description}</p>
  </div>
);

/* Helper Components */

const StatCard = ({ title, value, subtext, icon, color }: any) => {
  const colorMap: any = {
    teal: "bg-teal-50/50 text-teal-600",
    indigo: "bg-indigo-50/50 text-indigo-600",
    emerald: "bg-emerald-50/50 text-emerald-600",
    amber: "bg-amber-50/50 text-amber-600"
  };

  return (
    <div className="bg-white rounded-[2rem] p-7 border border-slate-100 shadow-sm transition-all hover:shadow-md group">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-2xl ${colorMap[color]} group-hover:scale-110 transition-transform`}>
          {icon}
        </div>
        <div className="p-1 px-2.5 bg-slate-50 rounded-lg text-slate-400">
          <ArrowUpRight size={14} />
        </div>
      </div>
      <div>
        <p className="text-slate-500 text-xs font-medium uppercase tracking-widest mb-1">{title}</p>
        <p className="text-3xl font-bold text-slate-900">{value}</p>
        <p className="text-[11px] text-slate-400 mt-2 font-medium">{subtext}</p>
      </div>
    </div>
  );
};

const ManagementLink = ({ to, title, description, icon, color }: any) => {
  const colors: any = {
    teal: "bg-teal-50 text-teal-600",
    indigo: "bg-indigo-50 text-indigo-600",
    emerald: "bg-emerald-50 text-emerald-600",
    blue: "bg-blue-50 text-blue-600",
    slate: "bg-slate-50 text-slate-600",
    violet: "bg-violet-50 text-violet-600"
  };

  return (
    <Link to={to} className="flex items-center gap-4 p-5 bg-white border border-slate-100 rounded-3xl hover:border-teal-500/20 hover:shadow-xl hover:shadow-slate-200/50 transition-all group">
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${colors[color]} group-hover:scale-105 transition-transform`}>
        {icon}
      </div>
      <div>
        <h4 className="text-sm font-bold text-slate-800">{title}</h4>
        <p className="text-xs text-slate-400 mt-0.5">{description}</p>
      </div>
      <ChevronRight size={16} className="ml-auto text-slate-300 group-hover:text-teal-500 group-hover:translate-x-1 transition-all" />
    </Link>
  );
};

const ProgressItem = ({ label, value, total, color }: any) => {
  const percentage = Math.min((value / total) * 100, 100);
  return (
    <div>
      <div className="flex justify-between items-end mb-2">
        <span className="text-xs font-medium text-slate-400">{label}</span>
        <span className="text-sm font-bold">{value.toLocaleString()}</span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} rounded-full transition-all duration-1000`}
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    </div>
  );
};

const PulseItem = ({ icon, title, value, color }: any) => {
  const colorMap: any = {
    teal: "bg-teal-500",
    indigo: "bg-indigo-500",
    emerald: "bg-emerald-500"
  };

  return (
    <div className="flex items-center justify-between p-3 rounded-2xl hover:bg-slate-50 transition-colors cursor-default">
      <div className="flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${colorMap[color]} animate-pulse`}></div>
        <div className="text-slate-400">{icon}</div>
        <span className="text-sm font-medium text-slate-600">{title}</span>
      </div>
      <span className="text-sm font-bold text-slate-900">{value}</span>
    </div>
  );
};

export default SuperAdminDashboard;
